import { OVERVIEW_SLIDE_WIDTH } from '@mentimeter/editor-overview-ui/constants';
import { SIDEBAR_PANE_WIDTH } from '@mentimeter/editor-properties-pane';
import { blackAlpha100 } from '@mentimeter/ragnar-colors';
import { useDeviceChecker } from '@mentimeter/ragnar-device';
import type { Extend } from '@mentimeter/ragnar-dsc';
import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
const containerHide = () => ({
  '0%': {
    visibility: 'visible',
  },
  '100%': {
    visibility: 'hidden',
  },
});

const overviewExtend: Extend = ({ theme }) => ({
  '&.hiddenoverview': {
    transform: `translateX(-${OVERVIEW_SLIDE_WIDTH}px)`,
  },
  '@media (prefers-reduced-motion: no-preference)': {
    transition: `transform ${theme.durations[1]}s ease`,
  },
});

interface EditorLayoutProps {
  canvas: ReactNode;
  slidesOverview: ReactNode;
  sidebar: ReactNode;
  slideOverviewToggle: ReactNode;
  isOverviewVisible: boolean;
  isSidePaneOpen: boolean;
  onOutsideCanvasClick?: () => void;
}

export const EditorLayout = ({
  canvas,
  slidesOverview,
  sidebar,
  slideOverviewToggle,
  isOverviewVisible,
  isSidePaneOpen,
  onOutsideCanvasClick,
}: EditorLayoutProps) => {
  const { renderer } = useRagnar();
  const { isSmallDevice } = useDeviceChecker();
  const t = useTranslations('creation.editor_layout');

  return (
    <Box
      as="main"
      flex="1 1 auto"
      flexDirection="row"
      width="100%"
      height="100%"
      bg="surfaceSunken"
      alignItems="stretch"
      pt="space4"
      pr="space4"
    >
      <Box
        as="section"
        aria-label={t('slide_overview_a11y')}
        id="editor-overview"
        position={isSmallDevice ? 'absolute' : 'relative'}
        className={
          !isSmallDevice || isOverviewVisible ? undefined : 'hiddenoverview'
        }
        height="100%"
        zIndex={[40, null, 'auto']}
        overflow="visible"
        flexDirection="row-reverse"
        extend={overviewExtend}
      >
        {isSmallDevice && (
          <Box position="absolute" top={0} right={0}>
            {slideOverviewToggle}
          </Box>
        )}
        <Box
          id="editor-overview-container"
          className={
            !isSmallDevice || isOverviewVisible ? undefined : 'hiddenoverview'
          }
          bg={isSmallDevice ? 'bg' : 'none'}
          height="100%"
          width={OVERVIEW_SLIDE_WIDTH}
          extend={({ theme }) => ({
            userSelect: 'none',
            borderRight:
              isSmallDevice && isOverviewVisible
                ? `1px solid ${blackAlpha100}`
                : 'none',
            '@media (prefers-reduced-motion: no-preference)': {
              '&.hiddenoverview': {
                animation: renderer
                  ? `${renderer.renderKeyframe(containerHide, {})} 0s ${
                      theme.durations[1]
                    }s forwards`
                  : undefined,
              },
            },
            '@media (prefers-reduced-motion: reduce)': {
              '&.hiddenoverview': {
                visibility: 'hidden',
              },
            },
          })}
        >
          {slidesOverview}
        </Box>
      </Box>
      <Box
        id="editor-canvas"
        flex="1 1 auto"
        /**
         * The small padding changes on `pt` and `pl` are necessary
         * to fix a small layout shift related to the hidden scrollbars.
         * @see https://github.com/mentimeter/mm-js/pull/28371
         */
        pt="2px"
        pl="2px"
        width="100%"
        onClick={onOutsideCanvasClick}
        extend={() => ({
          /**
           * Layout shifts due to toggles and popovers enabling the scrollbar.
           * @see https://github.com/radix-ui/primitives/issues/1925#issuecomment-2499804807
           */
          overflowY: 'hidden',
          scrollbarGutter: 'stable',
        })}
      >
        {canvas}
      </Box>
      {sidebar && (
        <Box
          as="section"
          aria-label={t('sidebar_a11y')}
          id="content-step--id" // for new user tour
          position="static"
          width={['auto', null]}
          alignItems="stretch"
          extend={({ theme }) => ({
            overflowY: 'auto',
            paddingLeft:
              isSidePaneOpen && !isSmallDevice
                ? `${SIDEBAR_PANE_WIDTH + theme.space[3]}px`
                : undefined,
          })}
          borderStyle="solid"
          borderColor="borderWeak"
          ml="space4"
        >
          {sidebar}
        </Box>
      )}
    </Box>
  );
};
